import type { Product } from '@my/directory-api';
import { isGroupActive, parsePropertyGroups, type CargoDefinition, type CargoItemProductInput } from '@my/tms-api';
import type { CargoItemDraft, CargoProductLineDraft, CargoProductOption } from './types';

/**
 * The products an order of this cargo type may carry, or null when products
 * do not apply. Linked products (with their default quantities) when the
 * cargo type lists any; otherwise every active product the organisation has.
 */
export function buildCargoProductOptions(
  productsOn: boolean,
  definition: CargoDefinition | null,
  activeProducts: Product[] | undefined,
): CargoProductOption[] | null {
  if (!productsOn || !definition?.hasProducts) return null;
  const all = activeProducts ?? [];
  const links = definition.products ?? [];
  if (links.length === 0) return all.map((p) => toOption(p, null, null));
  const byId = new Map(all.map((p) => [p.id, p]));
  const out: CargoProductOption[] = [];
  for (const link of links) {
    const p = byId.get(link.productId);
    // A linked product since deactivated or deleted is not offered.
    if (p) out.push(toOption(p, link.defaultQuantity, link.quantityUnit));
  }
  return out;
}

function toOption(
  p: Product,
  defaultQuantity: number | null,
  quantityUnit: CargoProductOption['quantityUnit'],
): CargoProductOption {
  return {
    id: p.id,
    name: p.name,
    code: p.code,
    unNumber: p.adrUnNumber,
    ewcCode: p.ewcCode,
    wasteCodes: (p.wasteCodes ?? []).map((w) => ({
      code: w.code,
      description: w.description,
      hazardous: w.hazardous,
    })),
    densityKgPerL: p.densityKgPerL,
    defaultQuantity,
    quantityUnit,
  };
}

/** "Sulphuric Acid 96% · UN 1830", "Waste petrol · 13 07 02*", or the name. */
export function productOptionLabel(o: CargoProductOption): string {
  const suffix = o.unNumber ? `UN ${o.unNumber}` : o.ewcCode;
  return suffix ? `${o.name} · ${suffix}` : o.name;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Weight / volume to fill when a product is picked on a row: the cargo
 * type's default quantity for it, converted, with the product's density
 * filling the other one. Nothing when the row already has a weight or a
 * volume — a figure somebody typed is never overwritten.
 */
export function productPrefill(
  option: CargoProductOption,
  draft: CargoItemDraft,
): Pick<CargoItemDraft, 'weightKg' | 'volumeM3'> | Record<string, never> {
  if (draft.weightKg != null || draft.volumeM3 != null) return {};
  const qty = option.defaultQuantity;
  if (qty == null || option.quantityUnit == null) return {};

  let weightKg: number | null = null;
  let volumeM3: number | null = null;
  switch (option.quantityUnit) {
    case 'Kilograms': weightKg = qty; break;
    case 'Tonnes': weightKg = qty * 1000; break;
    case 'Litres': volumeM3 = qty / 1000; break;
    case 'CubicMetres': volumeM3 = qty; break;
  }

  const density = option.densityKgPerL; // kg per litre = tonnes per m³
  if (density && density > 0) {
    if (weightKg != null && volumeM3 == null) volumeM3 = weightKg / density / 1000;
    else if (volumeM3 != null && weightKg == null) weightKg = volumeM3 * 1000 * density;
  }
  return {
    weightKg: weightKg == null ? null : round3(weightKg),
    volumeM3: volumeM3 == null ? null : round3(volumeM3),
  };
}

/** The cargo type has Compartments — a unit can carry several products. */
export function cargoAllowsMultipleProducts(definition: CargoDefinition | null): boolean {
  if (!definition) return false;
  return isGroupActive('compartments', parsePropertyGroups(definition.propertyGroupsJson));
}

/** A line's default amount from the cargo type's link, in the line's own units. */
export function lineDefaultAmount(option: CargoProductOption): Pick<CargoProductLineDraft, 'amount' | 'unit'> | null {
  const qty = option.defaultQuantity;
  if (qty == null || option.quantityUnit == null) return null;
  switch (option.quantityUnit) {
    case 'Kilograms': return { amount: qty, unit: 'kg' };
    case 'Tonnes': return { amount: round3(qty * 1000), unit: 'kg' };
    case 'Litres': return { amount: qty, unit: 'L' };
    case 'CubicMetres': return { amount: round3(qty * 1000), unit: 'L' };
  }
}

/** Editor lines → the wire list, in order. */
export function toProductInputs(lines: CargoProductLineDraft[]): CargoItemProductInput[] {
  return lines.map((l) => ({
    id: l.id,
    productId: l.productId,
    weightKg: l.unit === 'kg' ? l.amount : null,
    volumeM3: l.unit === 'L' && l.amount != null ? round3(l.amount / 1000) : null,
  }));
}

/** Same products, same order, same amounts — the dirty check for a unit's lines. */
export function productLinesEqual(a: CargoProductLineDraft[], b: CargoProductLineDraft[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((l, i) => {
    const o = b[i];
    return !!o && l.id === o.id && l.productId === o.productId && l.amount === o.amount && l.unit === o.unit;
  });
}

/**
 * The unit's weight / volume when it carries several products: what its
 * lines add up to (the server does the same on save). Nothing with one line —
 * its weight is the unit's own.
 */
export function unitTotalsFromLines(
  lines: CargoProductLineDraft[],
): Partial<Pick<CargoItemDraft, 'weightKg' | 'volumeM3'>> {
  if (lines.length < 2) return {};
  const kg = lines.filter((l) => l.unit === 'kg' && l.amount != null);
  const litres = lines.filter((l) => l.unit === 'L' && l.amount != null);
  return {
    ...(kg.length ? { weightKg: round3(kg.reduce((s, l) => s + (l.amount ?? 0), 0)) } : {}),
    ...(litres.length ? { volumeM3: round3(litres.reduce((s, l) => s + (l.amount ?? 0), 0) / 1000) } : {}),
  };
}

let lineKeySeq = 0;
/** A fresh editor-only key for a new line. */
export function newLineKey(): string {
  lineKeySeq += 1;
  return `new-${Date.now()}-${lineKeySeq}`;
}
